import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { apiRequest } from "../config/api";

const formatBlockedAt = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : format(date, "dd.MM.yyyy HH:mm");
};

interface BlockedIp {
  id: string;
  ip: string;
  reason?: string;
  playerId?: string | null;
  playerName?: string | null;
  blockedBy?: string | null;
  blockedAt?: string;
}

interface BlockedDevice {
  id: string;
  deviceId: string;
  reason?: string;
  playerId?: string | null;
  playerName?: string | null;
  blockedBy?: string | null;
  blockedAt?: string;
}

function BlockedIPs() {
  const [blockedIps, setBlockedIps] = useState<BlockedIp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newIp, setNewIp] = useState("");
  const [newReason, setNewReason] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  const [removing, setRemoving] = useState<string | null>(null);

  const [blockedDevices, setBlockedDevices] = useState<BlockedDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [devicesError, setDevicesError] = useState("");
  const [removingDevice, setRemovingDevice] = useState<string | null>(null);

  const fetchBlockedIps = async () => {
    setLoading(true);
    try {
      const response = await apiRequest("/api/admin/blocked-ips");
      if (response.ok) {
        const data = await response.json();
        setBlockedIps(data);
        setError("");
      } else {
        setError("Failed to load blocked IPs");
      }
    } catch (err) {
      console.error("Error fetching blocked IPs:", err);
      setError("Failed to load blocked IPs");
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedDevices = async () => {
    setDevicesLoading(true);
    try {
      const response = await apiRequest("/api/admin/blocked-devices");
      if (response.ok) {
        const data = await response.json();
        setBlockedDevices(data);
        setDevicesError("");
      } else {
        setDevicesError("Failed to load blocked devices");
      }
    } catch (err) {
      console.error("Error fetching blocked devices:", err);
      setDevicesError("Failed to load blocked devices");
    } finally {
      setDevicesLoading(false);
    }
  };

  const handleRemoveDevice = async (deviceId: string) => {
    setRemovingDevice(deviceId);
    try {
      const response = await apiRequest(`/api/admin/blocked-devices/${encodeURIComponent(deviceId)}`, {
        method: "DELETE",
      });
      if (response.ok) {
        fetchBlockedDevices();
      } else {
        const errorData = await response.json();
        setDevicesError(errorData.error || "Failed to unblock device");
      }
    } catch (err) {
      console.error("Error unblocking device:", err);
      setDevicesError("Failed to unblock device");
    } finally {
      setRemovingDevice(null);
    }
  };

  useEffect(() => {
    fetchBlockedIps();
    fetchBlockedDevices();
  }, []);

  const handleAdd = async () => {
    if (!newIp.trim()) return;
    setAddLoading(true);
    try {
      const response = await apiRequest("/api/admin/blocked-ips", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip: newIp.trim(), reason: newReason.trim() || undefined }),
      });
      if (response.ok) {
        setAddDialogOpen(false);
        setNewIp("");
        setNewReason("");
        fetchBlockedIps();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to block IP");
      }
    } catch (err) {
      console.error("Error blocking IP:", err);
      setError("Failed to block IP");
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async (ip: string) => {
    setRemoving(ip);
    try {
      const response = await apiRequest(`/api/admin/blocked-ips/${encodeURIComponent(ip)}`, {
        method: "DELETE",
      });
      if (response.ok) {
        fetchBlockedIps();
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to unblock IP");
      }
    } catch (err) {
      console.error("Error unblocking IP:", err);
      setError("Failed to unblock IP");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Blocked IPs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            IPs blocked here are rejected site-wide (login, API, everything) with a 403.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Block an IP
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>IP Address</TableCell>
                <TableCell>Player</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Blocked By</TableCell>
                <TableCell>Blocked At</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : blockedIps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No IPs are currently blocked.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                blockedIps.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell sx={{ fontFamily: "monospace" }}>{entry.ip}</TableCell>
                    <TableCell>{entry.playerName || "—"}</TableCell>
                    <TableCell>{entry.reason || "—"}</TableCell>
                    <TableCell>{entry.blockedBy || "—"}</TableCell>
                    <TableCell>
                      {formatBlockedAt(entry.blockedAt)}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        title="Unblock"
                        disabled={removing === entry.ip}
                        onClick={() => handleRemove(entry.ip)}
                      >
                        {removing === entry.ip ? (
                          <CircularProgress size={18} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Block an IP</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="IP Address"
            value={newIp}
            onChange={(e) => setNewIp(e.target.value)}
            placeholder="e.g. 203.0.113.42 (or exitIp|reportedIp behind a proxy)"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reason (optional)"
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleAdd}
            disabled={!newIp.trim() || addLoading}
            startIcon={addLoading ? <CircularProgress size={18} /> : undefined}
          >
            {addLoading ? "Blocking..." : "Block"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" fontWeight="bold">
          Blocked Devices
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Catches a flagged account even when it browses without logging in or rotates IPs,
          as long as it's the same browser. Populated automatically by the auto-block toggle
          on a player's Login IPs dialog - manual entries here are rarely needed.
        </Typography>

        {devicesError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDevicesError("")}>
            {devicesError}
          </Alert>
        )}

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Player</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Blocked By</TableCell>
                  <TableCell>Blocked At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devicesLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : blockedDevices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No devices are currently blocked.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  blockedDevices.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{entry.deviceId}</TableCell>
                      <TableCell>{entry.playerName || "—"}</TableCell>
                      <TableCell>{entry.reason || "—"}</TableCell>
                      <TableCell>{entry.blockedBy || "—"}</TableCell>
                      <TableCell>
                        {formatBlockedAt(entry.blockedAt)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          title="Unblock"
                          disabled={removingDevice === entry.deviceId}
                          onClick={() => handleRemoveDevice(entry.deviceId)}
                        >
                          {removingDevice === entry.deviceId ? (
                            <CircularProgress size={18} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Box>
  );
}

export default BlockedIPs;
